
import { createContext, useContext, useState, useRef, useCallback, type ReactNode } from 'react';

export interface GlobalTrack {
  url: string;
  trackName: string;
  artistName: string;
  artistId: string;
  artistType: 'mock' | 'artist';
  accentColor: string;
  embedType: 'youtube' | 'spotify' | 'soundcloud' | 'niconico' | 'link' | 'none';
}

interface GlobalPlayerContextValue {
  track: GlobalTrack | null;
  isPlaying: boolean;
  isVisible: boolean;
  setTrack: (track: GlobalTrack) => void;
  play: () => void;
  pause: () => void;
  dismiss: () => void;
}

const GlobalPlayerContext = createContext<GlobalPlayerContextValue | null>(null);

export function GlobalPlayerProvider({ children }: { children: ReactNode }) {
  const [track, setTrackState] = useState<GlobalTrack | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const setTrack = useCallback((newTrack: GlobalTrack) => {
    setTrackState(newTrack);
    setIsPlaying(true);
    setIsVisible(true);
  }, []);

  const play = useCallback(() => setIsPlaying(true), []);
  const pause = useCallback(() => setIsPlaying(false), []);
  const dismiss = useCallback(() => {
    setIsVisible(false);
    setIsPlaying(false);
    setTrackState(null);
  }, []);

  return (
    <GlobalPlayerContext.Provider value={{ track, isPlaying, isVisible, setTrack, play, pause, dismiss }}>
      {children}
    </GlobalPlayerContext.Provider>
  );
}

export function useGlobalPlayer() {
  const ctx = useContext(GlobalPlayerContext);
  if (!ctx) throw new Error('useGlobalPlayer must be used within GlobalPlayerProvider');
  return ctx;
}
